"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React, { useState } from "react"

import { PIN_SETUP_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"

import InfoBox from "../../common/InfoBox"
import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"

const PinSetupStep: React.FC<StepComponentProps> = ({ onComplete, progressPercentage, onBack, errorHandler, stepId }) => {
  const pinText = PIN_SETUP_STEP_TEXTS

  const { pin } = useSignup()
  const [confirmPin, setConfirmPin] = useState("")
  const [confirmPinError, setConfirmPinError] = useState<string | null>(null)
  const [confirmPinTouched, setConfirmPinTouched] = useState(false)
  const [confirmPinBlurred, setConfirmPinBlurred] = useState(false)

  const isPinConfirmValid = pin.value === confirmPin && pin.value.length === 4
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    pin.setTouched(true)
    pin.setBlurred(true)
    setConfirmPinTouched(true)
    setConfirmPinBlurred(true)

    if (pin.value !== confirmPin) {
      setConfirmPinError(pinText.errorMessage)
      datadogRum.addError(new Error(`PIN mismatch error: ${pinText.errorMessage}`), {
        step: stepId,
        field: "confirmPin",
        action: "submitPin",
      })
      return
    }

    if (!pin.isValid || !isPinConfirmValid) {
      return
    }

    onComplete()
  }

  const handleConfirmPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 4)
    setConfirmPin(value)

    if (confirmPinTouched && confirmPinBlurred) {
      if (value !== pin.value) {
        setConfirmPinError(pinText.errorMessage)
      } else {
        setConfirmPinError(null)
      }
    }
  }

  const formErrorKey = errorHandler.error ? "has-error" : "no-error"

  return (
    <StepWrapper
      key={formErrorKey}
      title={pinText.title}
      subtitle={pinText.subtitle}
      progressPercentage={progressPercentage}
      isLoading={errorHandler.isLoading}
      formError={errorHandler.error}
      onSubmit={handleSubmit}
      submitDisabled={!pin.isValid || !isPinConfirmValid || errorHandler.isLoading}
      submitButtonText={pinText.buttonText}
      showBackButton={true}
      onBack={onBack}
      footerText={pinText.helpText}
    >
      <div>
        <InfoBox>
          <p>{pinText.infoText}</p>
        </InfoBox>

        <TextInput
          label={pinText.labels.pin}
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin.value}
          onChange={(e) => pin.setValue(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          onBlur={() => {
            pin.setTouched(true)
            pin.setBlurred(true)
            if (pin.errorMessage) {
              datadogRum.addError(new Error(`PIN validation error: ${pin.errorMessage}`), {
                step: stepId,
                field: "pin",
              })
            }
          }}
          onFocus={() => pin.setTouched(true)}
          isValid={pin.isValid}
          touched={pin.touched}
          blurred={pin.blurred}
          errorMessage={pin.errorMessage}
        />

        <TextInput
          label={pinText.labels.confirmPin}
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={confirmPin}
          onChange={handleConfirmPinChange}
          onBlur={() => {
            setConfirmPinTouched(true)
            setConfirmPinBlurred(true)
            if (confirmPin !== pin.value) {
              setConfirmPinError(pinText.errorMessage)
              datadogRum.addError(new Error(`Confirm PIN validation error: ${pinText.errorMessage}`), {
                step: stepId,
                field: "confirmPin",
              })
            } else {
              setConfirmPinError(null)
            }
          }}
          onFocus={() => setConfirmPinTouched(true)}
          isValid={isPinConfirmValid && confirmPinTouched}
          touched={confirmPinTouched}
          blurred={confirmPinBlurred}
          errorMessage={confirmPinError}
        />
      </div>
    </StepWrapper>
  )
}

export default PinSetupStep
